import { ApolloClient, ApolloLink, DefaultOptions, HttpLink, from, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

import { DEACTIVATED_USER_ERROR } from '@/modules/common/application/error-handling';
import { GetUserQuery } from '@/modules/common/types';
import Firebase from '@/services/firebase.service';

import { cache, cachePersistor } from './cache';
import { GET_INITIAL_STATE, GET_USER } from './queries';
import resolvers from './resolvers';

interface DefaultOptionsWithFirebase extends DefaultOptions {
  firebase: Firebase;
}

type ApolloClientProps = {
  uri: string;
  firebase: Firebase;
};

const initialState = {
  drawerOpen: true,
  userEmail: undefined,
};

const initializeApolloClient = async ({
  uri,
  firebase,
}: ApolloClientProps): Promise<ApolloClient<unknown>> => {
  // Write initial state
  cache.writeQuery({
    query: GET_INITIAL_STATE,
    data: initialState,
  });

  // Restore from localStorage
  await cachePersistor.restore();

  // Auth link for HTTP
  const authLink = setContext(async (_, { headers }) => {
    const currentUser = firebase.getAuth().currentUser;
    const token = currentUser ? await currentUser.getIdToken() : null;

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  // Optional custom middleware
  const middlewareLink = new ApolloLink((operation, forward) => {
    // Your custom logic
    return forward(operation);
  });

  // Error link
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        if (message.includes(DEACTIVATED_USER_ERROR)) {
          const data = cache.readQuery<GetUserQuery>({ query: GET_USER });
          if (data?.user) {
            cache.writeQuery({
              query: GET_USER,
              data: {
                ...data,
                user: {
                  ...data.user,
                  activated: false,
                },
              },
            });
          }
        }
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        );
      });
    }
    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
    }
  });

  // HTTP link
  const httpLink = authLink.concat(
    new HttpLink({
      uri,
      headers: {
        'Apollo-Require-Preflight': 'true',
      },
    })
  );

  // WebSocket link (for subscriptions)
  const wsUrl =
    uri.replace(/^https?:\/\//i, uri.startsWith('https') ? 'wss://' : 'ws://') + '/subscriptions';
  const wsLink = new GraphQLWsLink(
    createClient({
      url: wsUrl,
      connectionParams: async () => {
        const currentUser = firebase.getAuth().currentUser;
        const token = currentUser ? await currentUser.getIdToken() : null;
        return { authToken: token };
      },
    })
  );

  // Split between subscriptions (wsLink) and everything else (httpLink)
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLink
  );

  // Combine the links in order
  const link = from([errorLink, middlewareLink, splitLink]);

  const options: DefaultOptionsWithFirebase = {
    firebase,
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  };

  // Create the client
  const client = new ApolloClient({
    link,
    cache,
    resolvers,
    defaultOptions: options,
    devtools: {
      enabled: import.meta.env.VITE_ENV === 'development',
    },
  });

  return client;
};

export default initializeApolloClient;
